import React, { useEffect, useMemo, useState } from 'react';
import {
  AdminPortfolioHoldingDto,
  AdminUserDto,
  AdminWatchlistItemDto,
  adminApi,
  isUnauthorized,
} from '../../services/adminApi';
import { getAdminTheme } from './adminTheme';

interface AdminAlertsScreenProps {
  isDarkMode: boolean;
  token: string;
  onUnauthorized: () => void;
}

type AlertSource = 'Watchlist' | 'Portfolio';

type AdminAlertRow = {
  key: string;
  source: AlertSource;

  id: number;
  userId: number;
  username?: string | null;
  userGroup?: string | null;

  symbol: string;
  currentPrice?: number | null;
  changePct?: number | null;

  buyPriceAlert?: number | null;
  sellPriceAlert?: number | null;
  status: string;
  updatedAtUtc: string;

  // Portfolio-only
  portfolioId?: number;
  portfolioName?: string | null;
  shares?: number;
  buyPrice?: number;
  buyDateUtc?: string;
  sellDateUtc?: string | null;
  isClosed?: boolean;
};

const hasAnyAlert = (x: { buyPriceAlert?: number | null; sellPriceAlert?: number | null }): boolean => {
  const b = x.buyPriceAlert;
  const s = x.sellPriceAlert;
  const hasB = b != null && Number.isFinite(Number(b)) && Number(b) > 0;
  const hasS = s != null && Number.isFinite(Number(s)) && Number(s) > 0;
  return hasB || hasS;
};

const AdminAlertsScreen: React.FC<AdminAlertsScreenProps> = ({ isDarkMode, token, onUnauthorized }) => {
  const theme = useMemo(() => getAdminTheme(isDarkMode), [isDarkMode]);

  const colors = useMemo(
    () => ({
      card: theme.cardBg,
      border: theme.border,
      text: theme.text,
      subText: theme.subText,
      hover: theme.hoverBg,
      inputBg: theme.inputBg,
      primary: theme.primary,
      danger: theme.danger,
      radius: theme.radius,
    }),
    [theme]
  );

  const [watchlistItems, setWatchlistItems] = useState<AdminWatchlistItemDto[] | null>(null);
  const [portfolioItems, setPortfolioItems] = useState<AdminPortfolioHoldingDto[] | null>(null);
  const [users, setUsers] = useState<AdminUserDto[] | null>(null);

  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(() => new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [filters, setFilters] = useState({
    username: '',
    category: '',
    symbol: '',
    source: 'all' as 'all' | AlertSource,
    status: '',
    closed: 'all' as 'all' | 'open' | 'closed',
  });

  const inputStyle: React.CSSProperties = {
    width: '100%',
    height: 34,
    borderRadius: colors.radius,
    border: `1px solid ${colors.border}`,
    backgroundColor: colors.inputBg,
    padding: '0 10px',
    color: colors.text,
    outline: 'none',
    boxSizing: 'border-box',
    fontSize: 13,
    fontWeight: 700,
  };

  const buttonStyle = (variant: 'primary' | 'neutral' | 'danger', disabled?: boolean): React.CSSProperties => {
    const base: React.CSSProperties = {
      height: 34,
      padding: '0 12px',
      borderRadius: colors.radius,
      border: `1px solid ${colors.border}`,
      backgroundColor: colors.card,
      color: colors.text,
      cursor: disabled ? 'not-allowed' : 'pointer',
      fontSize: 13,
      fontWeight: 800,
      opacity: disabled ? 0.6 : 1,
      whiteSpace: 'nowrap',
    };

    if (variant === 'primary') return { ...base, border: 'none', backgroundColor: colors.primary, color: '#fff' };
    if (variant === 'danger') return { ...base, border: 'none', backgroundColor: colors.danger, color: '#fff' };
    return base;
  };

  const normalizeSymbol = (v: unknown): string => (v ?? '').toString().trim().toUpperCase();

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const list = await adminApi.listUsers(token);
        if (cancelled) return;
        setUsers(list);
      } catch (e) {
        if (isUnauthorized(e)) {
          onUnauthorized();
          return;
        }
        if (cancelled) return;
        setUsers(null);
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [token, onUnauthorized]);

  const userGroupByUserId = useMemo(() => {
    const map = new Map<number, string>();
    for (const u of users ?? []) {
      map.set(u.id, (u.userGroup ?? '').toString().trim());
    }
    return map;
  }, [users]);

  const refresh = async (opts?: { clearSelection?: boolean }) => {
    setLoading(true);
    setError(null);
    try {
      const [w, p] = await Promise.all([
        adminApi.listWatchlists(token, {
          username: filters.username.trim() || undefined,
          category: filters.category.trim() || undefined,
        }),
        adminApi.listPortfolioHoldings(token, {
          username: filters.username.trim() || undefined,
          category: filters.category.trim() || undefined,
          symbol: filters.symbol.trim() || undefined,
        }),
      ]);
      setWatchlistItems(w);
      setPortfolioItems(p);
      if (opts?.clearSelection) setSelectedKeys(new Set());
    } catch (e) {
      if (isUnauthorized(e)) {
        onUnauthorized();
        return;
      }
      setError(e instanceof Error ? e.message : 'Failed loading alerts');
      setWatchlistItems(null);
      setPortfolioItems(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh({ clearSelection: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const rows = useMemo((): AdminAlertRow[] => {
    const list: AdminAlertRow[] = [];

    for (const x of watchlistItems ?? []) {
      if (!hasAnyAlert(x)) continue;
      list.push({
        key: `w:${x.id}`,
        source: 'Watchlist',
        id: x.id,
        userId: x.userId,
        username: x.username,
        userGroup: userGroupByUserId.get(x.userId) ?? null,
        symbol: x.symbol,
        currentPrice: x.currentPrice ?? null,
        changePct: x.changePct ?? null,
        buyPriceAlert: x.buyPriceAlert ?? null,
        sellPriceAlert: x.sellPriceAlert ?? null,
        status: x.status,
        updatedAtUtc: x.updatedAtUtc,
      });
    }

    for (const x of portfolioItems ?? []) {
      if (!hasAnyAlert(x)) continue;
      list.push({
        key: `p:${x.id}`,
        source: 'Portfolio',
        id: x.id,
        userId: x.userId,
        username: x.username,
        userGroup: userGroupByUserId.get(x.userId) ?? null,
        portfolioId: x.portfolioId,
        portfolioName: x.portfolioName ?? null,
        symbol: x.symbol,
        shares: x.shares,
        buyPrice: x.buyPrice,
        currentPrice: x.currentPrice ?? null,
        buyPriceAlert: x.buyPriceAlert ?? null,
        sellPriceAlert: x.sellPriceAlert ?? null,
        status: x.status,
        isClosed: Boolean(x.isClosed),
        buyDateUtc: x.buyDateUtc,
        sellDateUtc: x.sellDateUtc ?? null,
        updatedAtUtc: x.updatedAtUtc,
      });
    }

    return list.sort((a, b) => new Date(b.updatedAtUtc).getTime() - new Date(a.updatedAtUtc).getTime());
  }, [portfolioItems, userGroupByUserId, watchlistItems]);

  const filteredRows = useMemo(() => {
    const symQ = normalizeSymbol(filters.symbol);
    const usernameQ = filters.username.trim().toLowerCase();
    const categoryQ = filters.category.trim().toLowerCase();
    const statusQ = filters.status.trim().toLowerCase();

    return rows.filter((r) => {
      if (filters.source !== 'all' && r.source !== filters.source) return false;

      if (usernameQ) {
        const u = (r.username ?? '').toString().trim().toLowerCase();
        if (!u.includes(usernameQ)) return false;
      }

      if (categoryQ) {
        const c = (r.userGroup ?? '').toString().trim().toLowerCase();
        if (!c.includes(categoryQ)) return false;
      }

      if (symQ) {
        if (!normalizeSymbol(r.symbol).includes(symQ)) return false;
      }

      if (statusQ) {
        const s = (r.status ?? '').toString().trim().toLowerCase();
        if (!s.includes(statusQ)) return false;
      }

      if (filters.closed !== 'all') {
        if (r.source !== 'Portfolio') {
          if (filters.closed === 'closed') return false;
        } else {
          const isClosed = Boolean(r.isClosed);
          if (filters.closed === 'open' && isClosed) return false;
          if (filters.closed === 'closed' && !isClosed) return false;
        }
      }

      return true;
    });
  }, [filters.category, filters.closed, filters.source, filters.status, filters.symbol, filters.username, rows]);

  const usernameOptions = useMemo(() => {
    const list = (users ?? [])
      .map((u) => (u.username ?? '').toString().trim())
      .filter((u) => u.length > 0);
    return Array.from(new Set(list)).sort((a, b) => a.localeCompare(b));
  }, [users]);

  const categoryOptions = useMemo(() => {
    const list = (users ?? [])
      .map((u) => (u.userGroup ?? '').toString().trim())
      .filter((c) => c.length > 0);
    return Array.from(new Set(list)).sort((a, b) => a.localeCompare(b));
  }, [users]);

  const statusOptions = useMemo(() => {
    const list = rows.map((r) => (r.status ?? '').toString().trim()).filter((s) => s.length > 0);
    return Array.from(new Set(list)).sort((a, b) => a.localeCompare(b));
  }, [rows]);

  const columns = useMemo(
    () =>
      [
        { key: 'select', title: '', width: 48 },
        { key: 'source', title: 'Source', width: 110 },
        { key: 'id', title: 'ID', width: 90 },
        { key: 'userId', title: 'User ID', width: 110 },
        { key: 'username', title: 'Username', width: 160 },
        { key: 'category', title: 'Category', width: 160 },
        { key: 'portfolioName', title: 'Portfolio', width: 200 },
        { key: 'symbol', title: 'Symbol', width: 110 },
        { key: 'currentPrice', title: 'Current Price', width: 140 },
        { key: 'changePct', title: 'Change %', width: 120 },
        { key: 'buyPriceAlert', title: 'Buy Alert', width: 120 },
        { key: 'sellPriceAlert', title: 'Sell Alert', width: 120 },
        { key: 'status', title: 'Status', width: 150 },
        { key: 'isClosed', title: 'Closed', width: 90 },
        { key: 'updatedAtUtc', title: 'Updated', width: 200 },
      ] as const,
    []
  );

  const cellStyle: React.CSSProperties = {
    padding: '10px 12px',
    borderBottom: `1px solid ${colors.border}`,
    fontSize: 13,
    color: colors.text,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  };

  const headerCellStyle: React.CSSProperties = {
    ...cellStyle,
    position: 'sticky',
    top: 0,
    backgroundColor: isDarkMode ? 'rgba(255,255,255,0.03)' : '#f9fafb',
    zIndex: 1,
    fontSize: 12,
    fontWeight: 900,
    color: colors.subText,
    textAlign: 'left',
  };

  const clearSelectedAlerts = async () => {
    const keys = Array.from(selectedKeys);
    if (keys.length === 0) return;

    const ok = window.confirm(`Clear alerts for ${keys.length} selected row(s)?`);
    if (!ok) return;

    setLoading(true);
    setError(null);

    try {
      const byKey = new Map<string, AdminAlertRow>();
      for (const r of rows) byKey.set(r.key, r);

      const watchlistIds: number[] = [];

      for (const key of keys) {
        const row = byKey.get(key);
        if (!row) continue;

        if (row.source === 'Watchlist') {
          watchlistIds.push(row.id);
        } else {
          if (row.portfolioId == null || row.shares == null || row.buyPrice == null || !row.buyDateUtc) {
            continue;
          }

          // eslint-disable-next-line no-await-in-loop
          await adminApi.upsertPortfolioHolding(token, {
            id: row.id,
            userId: row.userId,
            portfolioId: row.portfolioId,
            symbol: normalizeSymbol(row.symbol),
            shares: row.shares,
            buyPrice: row.buyPrice,
            currentPrice: row.currentPrice ?? null,
            buyPriceAlert: null,
            sellPriceAlert: null,
            status: 'Not Reached',
            isClosed: row.isClosed ?? false,
            buyDateUtc: row.buyDateUtc,
            sellDateUtc: row.sellDateUtc ?? null,
          });
        }
      }

      if (watchlistIds.length > 0) {
        await adminApi.clearWatchlistAlerts(token, watchlistIds);
      }

      await refresh({ clearSelection: true });
    } catch (e) {
      if (isUnauthorized(e)) {
        onUnauthorized();
        return;
      }
      setError(e instanceof Error ? e.message : 'Failed clearing alerts');
    } finally {
      setLoading(false);
    }
  };

  const clearFilteredAlerts = async () => {
    const rowsToClear = filteredRows;
    if (rowsToClear.length === 0) return;

    const ok = window.confirm(`Clear alerts for ALL ${rowsToClear.length} row(s) currently shown in the table?`);
    if (!ok) return;

    setLoading(true);
    setError(null);

    try {
      const watchlistIds = rowsToClear.filter((r) => r.source === 'Watchlist').map((r) => r.id);
      if (watchlistIds.length > 0) {
        await adminApi.clearWatchlistAlerts(token, watchlistIds);
      }

      for (const row of rowsToClear) {
        if (row.source === 'Portfolio') {
          if (row.portfolioId == null || row.shares == null || row.buyPrice == null || !row.buyDateUtc) {
            continue;
          }

          // eslint-disable-next-line no-await-in-loop
          await adminApi.upsertPortfolioHolding(token, {
            id: row.id,
            userId: row.userId,
            portfolioId: row.portfolioId,
            symbol: normalizeSymbol(row.symbol),
            shares: row.shares,
            buyPrice: row.buyPrice,
            currentPrice: row.currentPrice ?? null,
            buyPriceAlert: null,
            sellPriceAlert: null,
            status: 'Not Reached',
            isClosed: row.isClosed ?? false,
            buyDateUtc: row.buyDateUtc,
            sellDateUtc: row.sellDateUtc ?? null,
          });
        }
      }

      await refresh({ clearSelection: true });
    } catch (e) {
      if (isUnauthorized(e)) {
        onUnauthorized();
        return;
      }
      setError(e instanceof Error ? e.message : 'Failed clearing alerts');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        backgroundColor: colors.card,
        border: `1px solid ${colors.border}`,
        borderRadius: colors.radius,
        padding: 14,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 900, color: colors.text, marginBottom: 2 }}>Alerts</div>
          <div style={{ fontSize: 13, fontWeight: 700, color: colors.subText }}>View & clear price alerts across watchlists and portfolios</div>
        </div>

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button type="button" style={buttonStyle('neutral')} onClick={() => void refresh({ clearSelection: true })} disabled={loading}>
            {loading ? 'Loading…' : 'Refresh'}
          </button>
          <button
            type="button"
            style={buttonStyle('danger', loading || selectedKeys.size === 0)}
            disabled={loading || selectedKeys.size === 0}
            onClick={() => void clearSelectedAlerts()}
          >
            Clear Alerts ({selectedKeys.size})
          </button>
          <button
            type="button"
            style={buttonStyle('danger', loading || filteredRows.length === 0)}
            disabled={loading || filteredRows.length === 0}
            onClick={() => void clearFilteredAlerts()}
          >
            Clear All (Filtered: {filteredRows.length})
          </button>
        </div>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '200px 200px 160px 160px 200px 1fr',
          gap: 10,
          marginBottom: 12,
        }}
      >
        <div>
          <div style={{ fontSize: 12, fontWeight: 900, color: colors.subText, marginBottom: 6 }}>Username</div>
          <input
            value={filters.username}
            onChange={(e) => setFilters((p) => ({ ...p, username: e.target.value }))}
            style={inputStyle}
            placeholder="Select or type..."
            list="admin-alerts-username-options"
          />
          <datalist id="admin-alerts-username-options">
            {usernameOptions.map((u) => (
              <option key={u} value={u} />
            ))}
          </datalist>
        </div>
        <div>
          <div style={{ fontSize: 12, fontWeight: 900, color: colors.subText, marginBottom: 6 }}>Category</div>
          <input
            value={filters.category}
            onChange={(e) => setFilters((p) => ({ ...p, category: e.target.value }))}
            style={inputStyle}
            placeholder="Select or type..."
            list="admin-alerts-category-options"
          />
          <datalist id="admin-alerts-category-options">
            {categoryOptions.map((c) => (
              <option key={c} value={c} />
            ))}
          </datalist>
        </div>
        <div>
          <div style={{ fontSize: 12, fontWeight: 900, color: colors.subText, marginBottom: 6 }}>Symbol</div>
          <input value={filters.symbol} onChange={(e) => setFilters((p) => ({ ...p, symbol: e.target.value }))} style={inputStyle} placeholder="e.g. AAPL" />
        </div>
        <div>
          <div style={{ fontSize: 12, fontWeight: 900, color: colors.subText, marginBottom: 6 }}>Source</div>
          <select
            value={filters.source}
            onChange={(e) => setFilters((p) => ({ ...p, source: e.target.value as any }))}
            style={inputStyle}
          >
            <option value="all">All</option>
            <option value="Watchlist">Watchlist</option>
            <option value="Portfolio">Portfolio</option>
          </select>
        </div>
        <div>
          <div style={{ fontSize: 12, fontWeight: 900, color: colors.subText, marginBottom: 6 }}>Status</div>
          <input
            value={filters.status}
            onChange={(e) => setFilters((p) => ({ ...p, status: e.target.value }))}
            style={inputStyle}
            placeholder="Select or type..."
            list="admin-alerts-status-options"
          />
          <datalist id="admin-alerts-status-options">
            {statusOptions.map((s) => (
              <option key={s} value={s} />
            ))}
          </datalist>
        </div>
        <div>
          <div style={{ fontSize: 12, fontWeight: 900, color: colors.subText, marginBottom: 6 }}>Closed (portfolio)</div>
          <select
            value={filters.closed}
            onChange={(e) => setFilters((p) => ({ ...p, closed: e.target.value as any }))}
            style={inputStyle}
          >
            <option value="all">All</option>
            <option value="open">Open</option>
            <option value="closed">Closed</option>
          </select>
        </div>

        <div style={{ display: 'flex', alignItems: 'end', gap: 10 }}>
          <button type="button" style={buttonStyle('primary')} onClick={() => void refresh({ clearSelection: true })} disabled={loading}>
            Apply
          </button>
          <button
            type="button"
            style={buttonStyle('neutral')}
            onClick={() => {
              setFilters({ username: '', category: '', symbol: '', source: 'all', status: '', closed: 'all' });
              setSelectedKeys(new Set());
            }}
          >
            Clear
          </button>
        </div>
      </div>

      {error && <div style={{ marginBottom: 10, color: colors.danger, fontSize: 13, fontWeight: 800 }}>{error}</div>}

      <div style={{ overflowX: 'auto', border: `1px solid ${colors.border}`, borderRadius: colors.radius }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
          <colgroup>
            {columns.map((c) => (
              <col key={c.key} style={{ width: c.width }} />
            ))}
          </colgroup>
          <thead>
            <tr>
              {columns.map((c) => (
                <th key={c.key} style={c.key === 'select' ? { ...headerCellStyle, textAlign: 'center' } : headerCellStyle}>
                  {c.key === 'select' ? (
                    <input
                      type="checkbox"
                      checked={filteredRows.length > 0 && filteredRows.every((x) => selectedKeys.has(x.key))}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setSelectedKeys((prev) => {
                          const next = new Set(prev);
                          if (checked) filteredRows.forEach((x) => next.add(x.key));
                          else filteredRows.forEach((x) => next.delete(x.key));
                          return next;
                        });
                      }}
                    />
                  ) : (
                    c.title
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredRows.map((r, idx) => {
              const isSelected = selectedKeys.has(r.key);
              return (
                <tr key={r.key} style={{ backgroundColor: idx % 2 === 0 ? 'transparent' : isDarkMode ? 'rgba(255,255,255,0.02)' : '#fcfcfd' }}>
                  <td style={{ ...cellStyle, textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setSelectedKeys((prev) => {
                          const next = new Set(prev);
                          if (checked) next.add(r.key);
                          else next.delete(r.key);
                          return next;
                        });
                      }}
                    />
                  </td>
                  <td style={cellStyle}>{r.source}</td>
                  <td style={cellStyle}>{r.id}</td>
                  <td style={cellStyle}>{r.userId}</td>
                  <td style={cellStyle}>{r.username ?? ''}</td>
                  <td style={cellStyle}>{r.userGroup ?? ''}</td>
                  <td style={cellStyle}>{r.source === 'Portfolio' ? (r.portfolioName ?? '') : ''}</td>
                  <td style={{ ...cellStyle, fontWeight: 900 }}>{normalizeSymbol(r.symbol)}</td>
                  <td style={cellStyle}>{r.currentPrice == null ? '—' : Number(r.currentPrice).toFixed(2)}</td>
                  <td style={cellStyle}>
                    {r.source === 'Watchlist' ? (r.changePct == null ? '—' : `${Number(r.changePct).toFixed(2)}%`) : '—'}
                  </td>
                  <td style={cellStyle}>{r.buyPriceAlert == null ? '—' : Number(r.buyPriceAlert).toFixed(2)}</td>
                  <td style={cellStyle}>{r.sellPriceAlert == null ? '—' : Number(r.sellPriceAlert).toFixed(2)}</td>
                  <td style={cellStyle}>{r.status}</td>
                  <td style={cellStyle}>{r.source === 'Portfolio' ? (r.isClosed ? 'Yes' : 'No') : '—'}</td>
                  <td style={cellStyle}>{r.updatedAtUtc}</td>
                </tr>
              );
            })}

            {filteredRows.length === 0 && (
              <tr>
                <td colSpan={columns.length} style={{ padding: 14, color: colors.subText, fontSize: 13, fontWeight: 700 }}>
                  {loading ? 'Loading…' : 'No alerts found'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminAlertsScreen;
